import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { User, Package, MapPin, Heart, CreditCard, Star, LogOut, Settings, Truck, Shield } from 'lucide-react';
import { toast } from 'sonner';

export default function AccountPage() {
  const navigate = useNavigate();
  const { user, profile, updateProfile, logout, isAuthenticated, isCourier, isAdmin } = useAuth();
  const [activeTab, setActiveTab] = useState('profile');
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({ name: '', phone: '' });

  useEffect(() => {
    setEditForm({
      name: profile?.name || '',
      phone: profile?.phone || '',
    });
  }, [profile?.name, profile?.phone]);

  console.log('👤 AccountPage render isAuthenticated=', isAuthenticated, 'isCourier=', isCourier, 'isAdmin=', isAdmin);

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12">
        <div className="bg-white rounded-xl p-12 text-center max-w-md">
          <User className="w-24 h-24 mx-auto text-gray-300 mb-6" />
          <h2 className="font-manrope font-bold text-2xl mb-4">Giriş Yapın</h2>
          <p className="text-gray-600 mb-8">Hesap bilgilerinizi görüntülemek için giriş yapmanız gerekiyor.</p>
          <Link
            to="/giris"
            className="inline-block bg-trendyol-orange hover:bg-orange-600 text-white font-semibold px-8 py-3 rounded-lg transition-colors"
          >
            Giriş Yap
          </Link>
        </div>
      </div>
    );
  }

  const handleLogout = async () => {
    console.log('👤 AccountPage logout click');
    try {
      await logout();
      console.log('👤 AccountPage logout ok');
      navigate('/');
    } catch (e) {
      console.error('👤 AccountPage logout failed', e);
    }
  };

  const menuItems = [
    { id: 'profile', label: 'Hesap Bilgilerim', icon: User, link: '/hesabim' },
    { id: 'orders', label: 'Siparişlerim', icon: Package, link: '/siparislerim' },
    { id: 'addresses', label: 'Adreslerim', icon: MapPin, link: '/adreslerim' },
    { id: 'favorites', label: 'Favorilerim', icon: Heart, link: '/favorilerim' },
    { id: 'coupons', label: 'Kuponlarım', icon: CreditCard, link: '/kuponlarim' },
    { id: 'reviews', label: 'Değerlendirmelerim', icon: Star, link: '/degerlendirmelerim' },
  ];

  return (
    <div className="min-h-screen bg-gray-50 py-8 md:py-12" data-testid="account-page">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <h1 className="font-manrope font-bold text-2xl md:text-3xl mb-6">Hesabım</h1>

        <div className="grid lg:grid-cols-4 gap-6">
          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl p-6 mb-6">
              <div className="text-center mb-4">
                <div className="w-20 h-20 bg-trendyol-orange text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-3">
                  {(profile?.name || user?.email || 'K').charAt(0).toUpperCase()}
                </div>
                <h3 className="font-semibold text-lg">{profile?.name || 'Kullanıcı'}</h3>
                <p className="text-sm text-gray-600">{profile?.email || user?.email}</p>
              </div>

              {(isAdmin || isCourier) && (
                <div className="mt-4 grid grid-cols-1 gap-2">
                  {isAdmin && (
                    <Link
                      to="/admin/live-tracking"
                      className="flex items-center justify-center gap-2 rounded-xl border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-800 hover:bg-gray-50"
                      onClick={() => console.log('👤 AccountPage navigate admin panel')}
                    >
                      <Shield className="w-4 h-4" />
                      Admin Paneli
                    </Link>
                  )}
                  {isCourier && (
                    <Link
                      to="/courier"
                      className="flex items-center justify-center gap-2 rounded-xl border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-800 hover:bg-gray-50"
                      onClick={() => console.log('👤 AccountPage navigate courier panel')}
                    >
                      <Truck className="w-4 h-4" />
                      Kurye Paneli
                    </Link>
                  )}
                </div>
              )}
            </div>

            <div className="bg-white rounded-xl overflow-hidden">
              <nav className="flex flex-col">
                {menuItems.map((item) => {
                  const Icon = item.icon;
                  return (
                    <Link
                      key={item.id}
                      to={item.link}
                      className={`flex items-center gap-3 px-6 py-4 hover:bg-gray-50 transition-colors border-b border-gray-100 ${
                        activeTab === item.id ? 'text-trendyol-orange bg-orange-50' : 'text-gray-700'
                      }`}
                      onClick={() => setActiveTab(item.id)}
                      data-testid={`menu-${item.id}`}
                    >
                      <Icon className="w-5 h-5" />
                      <span className="font-medium">{item.label}</span>
                    </Link>
                  );
                })}
                <button
                  onClick={handleLogout}
                  className="flex items-center gap-3 px-6 py-4 hover:bg-red-50 text-red-600 transition-colors"
                  data-testid="logout-button"
                >
                  <LogOut className="w-5 h-5" />
                  <span className="font-medium">Çıkış Yap</span>
                </button>
              </nav>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            <div className="bg-white rounded-xl p-6 md:p-8">
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-semibold text-xl">Hesap Bilgilerim</h2>
                <button
                  className="text-trendyol-orange hover:underline font-medium flex items-center gap-2"
                  onClick={() => setIsEditing((p) => !p)}
                >
                  <Settings className="w-4 h-4" />
                  Düzenle
                </button>
              </div>

              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-2">Ad Soyad</label>
                  {isEditing ? (
                    <input
                      type="text"
                      value={editForm.name}
                      onChange={(e) => setEditForm((p) => ({ ...p, name: e.target.value }))}
                      className="w-full border-2 border-gray-200 rounded-lg px-4 py-2 focus:border-trendyol-orange focus:outline-none"
                    />
                  ) : (
                    <p className="text-gray-900 font-medium">{profile?.name || '-'}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-2">E-posta</label>
                  <p className="text-gray-900 font-medium">{profile?.email || user?.email || '-'}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-2">Telefon</label>
                  {isEditing ? (
                    <input
                      type="tel"
                      value={editForm.phone}
                      onChange={(e) => setEditForm((p) => ({ ...p, phone: e.target.value }))}
                      className="w-full border-2 border-gray-200 rounded-lg px-4 py-2 focus:border-trendyol-orange focus:outline-none"
                    />
                  ) : (
                    <p className="text-gray-900 font-medium">{profile?.phone || 'Eklenmemiş'}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-2">Üyelik Tarihi</label>
                  <p className="text-gray-900 font-medium">
                    {profile?.created_at ? new Date(profile.created_at).toLocaleDateString('tr-TR') : '-'}
                  </p>
                </div>
              </div>

              {isEditing ? (
                <div className="mt-6 flex justify-end gap-2">
                  <button
                    className="px-4 py-2 border-2 border-gray-300 rounded-lg hover:border-gray-400"
                    onClick={() => {
                      setIsEditing(false);
                      setEditForm({ name: profile?.name || '', phone: profile?.phone || '' });
                    }}
                  >
                    Vazgeç
                  </button>
                  <button
                    className="px-4 py-2 bg-trendyol-orange hover:bg-orange-600 text-white font-semibold rounded-lg"
                    onClick={async () => {
                      try {
                        await updateProfile({ name: editForm.name, phone: editForm.phone });
                        toast.success('Bilgiler güncellendi');
                        setIsEditing(false);
                      } catch (e) {
                        toast.error('Güncelleme başarısız');
                      }
                    }}
                  >
                    Kaydet
                  </button>
                </div>
              ) : null}

              <div className="mt-8 pt-6 border-t border-gray-200">
                <h3 className="font-semibold text-lg mb-4">Hızlı Erişim</h3>
                <div className="grid md:grid-cols-3 gap-4">
                  <Link
                    to="/siparislerim"
                    className="flex items-center gap-3 p-4 border-2 border-gray-200 rounded-lg hover:border-trendyol-orange transition-colors"
                  >
                    <Package className="w-6 h-6 text-trendyol-orange" />
                    <div>
                      <p className="font-medium">Siparişlerim</p>
                      <p className="text-sm text-gray-600">0 Aktif Sipariş</p>
                    </div>
                  </Link>

                  <Link
                    to="/adreslerim"
                    className="flex items-center gap-3 p-4 border-2 border-gray-200 rounded-lg hover:border-trendyol-orange transition-colors"
                  >
                    <MapPin className="w-6 h-6 text-trendyol-orange" />
                    <div>
                      <p className="font-medium">Adreslerim</p>
                      <p className="text-sm text-gray-600">Adres Ekle</p>
                    </div>
                  </Link>

                  <Link
                    to="/kuponlarim"
                    className="flex items-center gap-3 p-4 border-2 border-gray-200 rounded-lg hover:border-trendyol-orange transition-colors"
                  >
                    <CreditCard className="w-6 h-6 text-trendyol-orange" />
                    <div>
                      <p className="font-medium">Kuponlarım</p>
                      <p className="text-sm text-gray-600">0 Aktif Kupon</p>
                    </div>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}